import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, ArrowRight, Sparkles, MapPin, MessageSquareHeart,
  ShieldCheck, Timer, ScanSearch, Home as HomeIcon,
  Home, CalendarCheck, KeyRound, Truck, Building, Briefcase,
  Building2, ChefHat, Bath, Feather, Layers, BadgeCheck, Clock,
} from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import WorkCard from "@/components/WorkCard";
import { Reveal, Stagger, StaggerItem, LineReveal, Chapter } from "@/components/Reveal";
import { BUSINESS, SERVICES, WHY_US, AREAS, WORK } from "@/data/site";

const ICONS = { Home, Sparkles, CalendarCheck, KeyRound, Truck, Building, Briefcase, Building2, ChefHat, Bath, Feather, Layers, BadgeCheck, Clock, ScanSearch, MapPin };

const VALUES = [
  { icon: ShieldCheck, title: "Responsibility", text: "We treat every home and business like our own — showing up prepared and following through on every detail." },
  { icon: Timer, title: "Punctuality", text: "Your time matters. We arrive when we say we will and work efficiently while we're there." },
  { icon: ScanSearch, title: "Attention to Detail", text: "From baseboards to fixtures, we focus on the small things that make a space feel truly clean." },
  { icon: HomeIcon, title: "Pleasant Spaces", text: "Our goal is simple: clean, organized, comfortable spaces you'll love walking back into." },
];

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <>
      <SEO
        title="Julisa's House Cleaning | Professional Cleaning Services in Kansas City"
        description="Julisa's House Cleaning provides reliable residential and commercial cleaning services across the Kansas City metro — Overland Park, Lenexa, Olathe, Kansas City KS and Kansas City MO. Request a free quote today."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0" aria-hidden="true">
          <img
            src="/images/hero-bg.jpg"
            alt=""
            className="-mt-[6%] h-[118%] w-full object-cover"
            loading="eager"
            width="1920"
            height="963"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-sky-100/40" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-white to-transparent" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <motion.div style={{ y: textY }} className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-white/80 backdrop-blur px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700 shadow-sm"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Homes & Businesses · Kansas City Metro
            </motion.p>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.08]">
              <LineReveal
                lines={[
                  "Professional Cleaning",
                  <>Services You Can <span className="text-sky-600 italic">Count On</span></>,
                ]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-700 leading-relaxed"
            >
              Reliable, detailed cleaning services for homes and businesses throughout the
              Kansas City area — with responsibility, punctuality, and care in every visit.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link
                to="/quote"
                data-testid="hero-get-quote-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-sky-600/30 transition-all duration-200 hover:bg-sky-700 hover:shadow-xl hover:shadow-sky-600/40 active:scale-[0.98]"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky-300 bg-white/90 backdrop-blur px-7 py-4 text-base font-semibold text-sky-800 shadow-sm transition-all duration-200 hover:bg-white active:scale-[0.98]"
              >
                <Phone className="h-4 w-4 text-sky-600" aria-hidden="true" />
                Call {BUSINESS.phone}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <p className="flex items-center gap-2 text-sm text-sky-950/70 font-medium">
                <MapPin className="h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                Serving Overland Park · Lenexa · Olathe · Kansas City, KS · Kansas City, MO
              </p>
              <span className="hidden sm:block h-4 w-px bg-sky-300" aria-hidden="true" />
              <p className="flex items-center gap-2 text-sm text-sky-950/70 font-medium">
                <Sparkles className="h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                Free, no-pressure quotes
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* CHAPTER 01 — THE STANDARD */}
      <section className="py-20 sm:py-28 bg-sky-50/70" aria-labelledby="the-standard">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter
            index="01"
            label="The Julisa Standard"
            title="A cleaning company built on responsibility, punctuality, and detail."
            description="Julisa's House Cleaning is dedicated to providing professional cleaning services for homes and businesses across the Kansas City metro. We work with responsibility, punctuality, and attention to detail to make sure every space we touch is left clean, organized, and pleasant."
          />
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <StaggerItem key={v.title}>
                <div className="group h-full rounded-2xl border border-sky-200/70 bg-white p-7 shadow-sm shadow-sky-900/5 transition-all duration-300 hover:-translate-y-1 hover:border-sky-400 hover:shadow-xl hover:shadow-sky-600/10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-600 text-white shadow-md shadow-sky-600/25 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                    <v.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CHAPTER 02 — SERVICES PREVIEW */}
      <section className="bg-sky-100/60 py-20 sm:py-28 border-y border-sky-200/60" aria-labelledby="services-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter
            index="02"
            label="Our Services"
            title="Our most popular cleaning services."
            description="The services Kansas City asks for most — plus eight more on our full services page."
          />

          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.slice(0, 4).map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <StaggerItem key={s.slug}>
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${s.slug}`}
                    className="group block h-full rounded-2xl border border-sky-200/70 bg-white p-7 shadow-sm shadow-sky-900/5 transition-all duration-300 hover:-translate-y-1 hover:border-sky-400 hover:shadow-xl hover:shadow-sky-600/10"
                  >
                    <div className="flex items-start justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                        <Icon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <ArrowRight className="h-5 w-5 text-sky-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-sky-600" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-slate-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.blurb}</p>
                  </Link>
                </StaggerItem>
              );
            })}
          </Stagger>

          <Reveal delay={0.1} className="mt-10 text-center">
            <Link
              to="/services"
              data-testid="home-more-services-button"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 hover:shadow-xl hover:shadow-sky-600/30 active:scale-[0.98]"
            >
              More Services <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <p className="mt-3 text-sm text-slate-500">See all 13 services, including construction cleanup, office & commercial cleaning</p>
          </Reveal>
        </div>
      </section>

      {/* CHAPTER 03 — OUR WORK PREVIEW */}
      <section className="py-20 sm:py-28 bg-sky-50/70" aria-labelledby="our-work">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Chapter
              index="03"
              label="Our Work"
              title="Before & after — real results from real cleans."
              description="A few recent transformations from homes across the Kansas City area. This is the level of detail we bring to every visit."
            />
            <Reveal delay={0.2}>
              <Link
                to="/our-work"
                data-testid="home-view-gallery-link"
                className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 active:scale-[0.98]"
              >
                See the full gallery <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2">
            {WORK.slice(0, 4).map((w) => (
              <WorkCard key={w.src} work={w} />
            ))}
          </Stagger>
        </div>
      </section>

      {/* CHAPTER 04 — WHY CHOOSE US */}
      <section className="py-20 sm:py-28 bg-white" aria-labelledby="why-choose-us">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter
            center
            index="04"
            label="Why Choose Us"
            title="The kind of clean you notice — and service you can rely on."
          />
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {WHY_US.map((w) => {
              const Icon = ICONS[w.icon];
              return (
                <StaggerItem key={w.title}>
                  <div className="group h-full rounded-2xl border border-sky-200/70 bg-sky-50/70 p-6 text-center shadow-sm shadow-sky-900/5 transition-all duration-300 hover:-translate-y-1 hover:border-sky-400 hover:shadow-xl hover:shadow-sky-600/10">
                    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-600 text-white shadow-md shadow-sky-600/30 transition-transform duration-300 group-hover:scale-110">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 text-base font-bold text-slate-900">{w.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.blurb}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* CHAPTER 05 — SERVICE AREAS PREVIEW */}
      <section className="bg-gradient-to-br from-sky-600 via-sky-700 to-sky-800 py-20 sm:py-24" aria-labelledby="areas-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <Reveal>
                <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-sky-200">
                  <span className="font-display text-2xl tracking-normal text-sky-300" aria-hidden="true">05</span>
                  <span className="h-px w-8 bg-sky-300" aria-hidden="true" />
                  Service Areas
                </p>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-[1.12]">
                  Cleaning services across the Kansas City area.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link
                to="/service-areas"
                data-testid="home-view-areas-link"
                className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/25 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition-all duration-200 hover:bg-white/20 active:scale-[0.98]"
              >
                Explore service areas <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>

          <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {AREAS.map((a) => (
              <StaggerItem key={a.slug}>
                <Link
                  to="/service-areas"
                  data-testid={`home-area-card-${a.slug}`}
                  className="flex h-full items-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur transition-all duration-300 hover:bg-white/25 hover:-translate-y-0.5"
                >
                  <MapPin className="h-5 w-5 shrink-0 text-sky-200" aria-hidden="true" />
                  <span className="text-sm font-semibold text-white">{a.city}, {a.state}</span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* REVIEWS PLACEHOLDER */}
      <section className="py-20 sm:py-28 bg-sky-100/50" aria-labelledby="reviews">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Chapter
            center
            index="06"
            label="Customer Stories"
            title="Real reviews are on their way."
            description="We're collecting feedback from real customers right now. After your cleaning, we'd love to hear about your experience — this space will soon feature their words, not ours."
          />
          <Reveal delay={0.2}>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-reviews-facebook-button"
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-sky-600 px-6 py-3.5 text-sm font-semibold text-white shadow-md shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 active:scale-[0.98]"
            >
              <MessageSquareHeart className="h-4 w-4" aria-hidden="true" />
              Share your experience on Facebook
            </a>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 sm:py-28 bg-sky-50/70">
        <Reveal className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-600 to-sky-800 px-6 py-14 sm:px-14 text-center shadow-2xl shadow-sky-600/25">
            <div className="pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
            <h2 className="relative font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
              Ready for a cleaner, <span className="text-sky-200 italic">more pleasant</span> space?
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-base text-sky-100">
              Tell us about your home or business and we'll put together a free, no-pressure quote.
            </p>
            <div className="relative mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/quote"
                data-testid="home-cta-quote-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 text-base font-semibold text-sky-700 shadow-lg transition-all duration-200 hover:bg-sky-50 active:scale-[0.98]"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="home-cta-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-all duration-200 hover:bg-white/20 active:scale-[0.98]"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> Call Now
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
